import _ from 'underscore';
import {
  getPerformanceColumns,
  conversionsColumns,
  conversionsColumnsWithFluctuation,
  getConversionColumns,
  competitiveColumns,
  callDetailsColumns,
  ColumnAveragePosition,
  ColumnCallForwarding,
  ColumnClickSharePercent,
  ColumnTotalSpend,
  ColumnTopImpressionRate,
  ColumnAbsoluteTopImpressionRate,
  ColumnAuctionLostToAdQualityPercent,
  ColumnAuctionLostToLandingPercent,
  ColumnAuctionLostToBidPercent,
  ColumnExactMatchImpressionSharePercent,
  ColumnTopImpressionSharePercent,
  ColumnTopImpressionShareLostToBudget,
  ColumnTopImpressionShareLostToRank,
  ColumnAbsoluteTopImpressionSharePercent,
  ColumnAbsoluteTopImpressionShareLostToBudget,
  ColumnAbsoluteTopImpressionShareLostToRank,
  ColumnViewThroughConversions,
  ColumnViewThroughConversionsCredit,
  ColumnAudienceAuctionWonPercent,
  ColumnAudienceTopISLostToBudgetPercent,
  ColumnAudienceTopISLostToRankPercent,
  ColumnRelativeCTR,
  ColumnViewThroughConversionsRevenue,
  ColumnViewThroughConversionsCPA,
  ColumnViewThroughPartialConversionsCPA,
  ColumnViewThroughConversionsReturnOnAdSpend,
  ColumnViewThroughConversionsRate,
  ColumnViewThroughPartialConversionsRate,
  ColumnNewCustomerConversions,
  ColumnNewCustomerRevenue,
  ColumnNewCustomerConversionRate,
  ColumnNewCustomerCPA,
  ColumnNewCustomerRevenueOnAdSpend,
} from '@bingads-webui-campaign/campaignui-common-columns';
import { getColumnConversions } from '@bingads-webui-campaign/column-conversions';

class CategoryConfigBuilder {
  /**
   * Builder class helps to generate a category config for projection-grid
   * column chooser plugin
   * @param {Object} options -
   * @param {ColumnsConfigBuilder} options.columnsBuilder - The parent columns builder
   * @param {string} options.name - Name of the category
   */
  constructor({ columnsBuilder, name }) {
    this.columnsBuilder = columnsBuilder;
    this.name = name;
    this.columns = [];
  }

  /**
   * Add a single column to the category
   * @param {Class} Column - The column class to Add
   * @returns {CategoryConfigBuilder} - The builder itself
   */
  addColumn(Column) {
    this.columns.push(Column);
    return this;
  }

  /**
   * Add a list of columns to the category
   * @param {Class[]} columns - The column classes
   * @returns {CategoryConfigBuilder} - The builder itself
   */
  addColumns(columns) {
    _.each(columns, Column => this.addColumn(Column));
    return this;
  }

  /**
   * Get the category config
   * @param {Object} options -
   * @param {I18n} options.i18n - I18n provider
   * @param {permissions} options.permissions - the permissions
   * @returns {Object} - The category config
   */
  getCategoryConfig({ i18n, permissions }) {
    return {
      name: i18n.getString(this.name),
      options: _.map(this.columns, (Column) => {
        const { name, title } = new Column({ i18n, permissions });
        return {
          key: name,
          name: title,
          selectOption: this.columnsBuilder.isColumnLocked(Column) ? 'locked' : 'optional',
        };
      }),
    };
  }
}

export class ColumnsConfigBuilder {
  /**
   * Builder class helps to generate columns config and column chooser plugin
   * config for projection-grid
   * @param {Object} options -
   * @param {Object} options.Permissions - Permissions
   */
  constructor(options) {
    this.categories = [];
    this.locked = [];
    this.helpIds = {};

    let IsVideoAdsPerfMetricsEnabled = false; //eslint-disable-line
    let isCPSEnabled = false; //eslint-disable-line
    let isInstallPerformanceColumnsEnabled = false; //eslint-disable-line
    let isAppCampaignNewCampaignGridColumnsEnabled = false;
    let isNewCustomerAcquisitionColumnsEnabled = false; //eslint-disable-line
    try {
      IsVideoAdsPerfMetricsEnabled = options.Permissions.IsVideoAdsPerfMetricsEnabled; //eslint-disable-line
      isCPSEnabled = options.Permissions.dynamic && options.Permissions.dynamic.CPSReporting; //eslint-disable-line
      isInstallPerformanceColumnsEnabled = options.Permissions.dynamic && options.Permissions.dynamic.InstallPerformanceColumns; //eslint-disable-line
      isAppCampaignNewCampaignGridColumnsEnabled = options.Permissions.dynamic && options.Permissions.dynamic.AppCampaignNewCampaignGridColumns; //eslint-disable-line
      isNewCustomerAcquisitionColumnsEnabled = options.Permissions.dynamic && options.Permissions.dynamic.NewCustomerAcquisition; //eslint-disable-line
    } catch (e) { } //eslint-disable-line

    this.isVideoAdsPerfMetricsEnabled = IsVideoAdsPerfMetricsEnabled;
    this.isCPSEnabled = isCPSEnabled;
    this.isInstallPerformanceColumnsEnabled = isInstallPerformanceColumnsEnabled;
    this.isAppCampaignNewCampaignGridColumnsEnabled = isAppCampaignNewCampaignGridColumnsEnabled;
    this.isNewCustomerAcquisitionColumnsEnabled = isNewCustomerAcquisitionColumnsEnabled;

    this.performanceColumns = getPerformanceColumns({
      IsVideoAdsPerfMetricsEnabled,
    });
  }

  /**
   * Add a column category
   * @param {string} name - Name of the category
   * @param {CategoryDirectorCallback} director - A callback to direct the category buidler
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addCategory(name, director) {
    const categoryBuilder = new CategoryConfigBuilder({ columnsBuilder: this, name });

    this.categories.push(categoryBuilder);
    /**
     * @callback ColumnsConfigBuilder
     * @param {CategoryConfigBuilder} categoryBuilder - The builder to create a column category
     * @returns {void}
     */
    director(categoryBuilder);

    return this;
  }

  /**
   * Set the locked columns
   * @param {Class[]} columns - The classes of the locked columns
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  setLockedColumns(columns) {
    this.locked.push(...columns);
    return this;
  }

  /**
   * Check if a clumn is locked
   * @param {Class} column - The column to check
   * @returns {boolean} - True if the column is locked
   */
  isColumnLocked(column) {
    return _.contains(this.locked, column);
  }

  /**
   * Set the selected columns
   * @param {Class[]} columns - The classes of the selected columns
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  setSelectedColumns(columns) {
    this.selected = columns;
    return this;
  }

  /**
   * Create the columns config
   * @param {Object} options - The column constructor options
   * @returns {Object[]} - A list of column config
   */
  getColumnsConfig(options) {
    return _.chain(this.categories)
      .map(_.property('columns'))
      .concat(this.locked)
      .flatten()
      .uniq()
      .map(Column => new Column(options).toJSON())
      .value();
  }

  /**
   * Create the columns config for column conversion
   * which compensate for legacy shared column oject
   * @param {Object} options - The column constructor options
   * @returns {Object[]} - A list of column config
   */
  getAllColumns(options) {
    return _.chain(this.categories)
      .map(_.property('columns'))
      .concat(this.locked)
      .flatten()
      .uniq()
      .map(Column => new Column(options))
      .value();
  }

  /**
   * Create the column chooser config
   * @param {Object} options -
   * @param {I18n} options.i18n - The i18n provider
   * @param {boolean} options.patchQuery -
   *    True if the column chooser will patch the query with $select option
   * @param {Object} options.Columns -
   *    The preference JSData resource to get saved columns
   * @param {Object} options.SelectedColumns -
   *    The preference JSData resource to get current selected columns
   * @param {Object} options.LegacyColumns -
   *    The preference JSData resource to get legacy saved columns
   * @param {Object} options.SelectedColumns -
   *    The preference JSData resource to get current selected columns
   * @param {Object} options.LegacyGridFlag
   *    The preference JSData resource to get the migration flag of legacy grid
   * @param {Object} options.nameFromLegacy
   *    The hash object to map legacy column name to new grid column name
   * @param {Object} options.mandatoryProperties -
   *    A property array which is not appear in column chooser but in $select option
   * @param {Object} options.CustomColumns -
   *    The preference JSData resource to get custom columns
   * @param {Object} options.customColumnConfig -
   *    A hash containing column chooser configuration for custom columns
   * @param {Object} options.permissions -
   *    The permissions, may be null.
   * @returns {Object} - The column chooser config object
   */
  getColumnChooserConfig({
    i18n,
    patchQuery,
    Columns,
    SelectedColumns,
    LegacyColumns,
    LegacySelectedColumns,
    LegacyGridFlag,
    nameFromLegacy,
    mandatoryProperties = [],
    CustomColumns,
    customColumnConfig,
    permissions = {},
  }) {
    const { dynamic = {} } = permissions;
    const {
      ColumnConversion: IsColumnConversionEnabled,
    } = dynamic;

    return {
      categories: _.map(
        this.categories,
        category => category.getCategoryConfig({ i18n, permissions })
      ),
      selected: _.map(this.selected, Column => new Column({ i18n }).name),
      locked: _.map(this.locked, Column => new Column({ i18n }).name),

      ...IsColumnConversionEnabled ? {
        columnConversions: getColumnConversions(this.getAllColumns({ i18n })),
      } : {},

      patchQuery: Boolean(patchQuery),
      Columns,
      SelectedColumns,
      LegacyColumns,
      LegacySelectedColumns,
      LegacyGridFlag,
      nameFromLegacy,
      i18n,
      mandatoryProperties,
      CustomColumns,
      customColumnConfig,
    };
  }

  // Helper function
  addCategoryWithoutSomeColumns(unfilteredColumns, categoryName, blacklist) {
    const filteredColumns =
      _.reject(unfilteredColumns, column => _.contains(blacklist, column));
    return this.addCategory(categoryName, category => category.addColumns(filteredColumns));
  }

  /**
   * Add the performance category for ad groups
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addPerformanceCategoryForAdGroups({
    IsAvgPositionDeprecationPhase2Enabled,
  }) {
    const perfColumns = getPerformanceColumns({
      IsVideoAdsPerfMetricsEnabled: this.isVideoAdsPerfMetricsEnabled,
      isCPSEnabled: this.isCPSEnabled,
    });
    const columns = _.chain(perfColumns)
      .union([
        ColumnTopImpressionRate,
        ColumnAbsoluteTopImpressionRate,
      ])
      .value();

    return this.addCategoryWithoutSomeColumns(columns, 'Performance', _.compact([
      IsAvgPositionDeprecationPhase2Enabled && ColumnAveragePosition,
    ]));
  }

  /**
   * Add the performance category for campaign
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addPerformanceCategoryForCampaign({
    withFluctuations,
    IsAvgPositionDeprecationPhase2Enabled,
    getCampaignTypeGlobalFilter,
  } = {}) {
    const campaignTypeGloabalFilter = _.isFunction(getCampaignTypeGlobalFilter) && getCampaignTypeGlobalFilter();

    // app campaign related perf columns to be included when global gilter is set to App campaign (128) or All (65535)
    const includeAppCampaignColumns = campaignTypeGloabalFilter === 128 || campaignTypeGloabalFilter === 65535;

    const perfColumns = getPerformanceColumns({
      withFluctuations,
      IsVideoAdsPerfMetricsEnabled: this.isVideoAdsPerfMetricsEnabled,
      isCPSEnabled: this.isCPSEnabled,
      isAppCampaignNewCampaignGridColumnsEnabled: this.isAppCampaignNewCampaignGridColumnsEnabled && includeAppCampaignColumns,
    });

    const columns = _.union(perfColumns, [
      ColumnTopImpressionRate,
      ColumnAbsoluteTopImpressionRate,
    ]);

    return this.addCategoryWithoutSomeColumns(columns, 'Performance', _.compact([
      IsAvgPositionDeprecationPhase2Enabled && ColumnAveragePosition,
    ]));
  }

  /**
   * Add the performance category for ads
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addPerformanceCategoryForAd({
    IsAvgPositionDeprecationPhase2Enabled,
    IsAvgPositionDeprecationPhase1Enabled,
  }) {
    const withProminence = _.union(
      this.performanceColumns,
      IsAvgPositionDeprecationPhase1Enabled ? [
        ColumnTopImpressionRate,
        ColumnAbsoluteTopImpressionRate,
      ] : []
    );
    return this.addCategoryWithoutSomeColumns(withProminence, 'Performance', _.compact([
      ColumnTotalSpend,
      IsAvgPositionDeprecationPhase2Enabled && ColumnAveragePosition,
    ]));
  }

  /**
   * Add the performance category for positive keywords grid
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addPerformanceCategoryForKeywords({
    IsAvgPositionDeprecationPhase2Enabled,
  }) {
    const columns = _.chain(this.performanceColumns)
      .union([
        ColumnTopImpressionRate,
        ColumnAbsoluteTopImpressionRate,
      ])
      .difference([
        ColumnTotalSpend,
      ])
      .value();

    return this.addCategoryWithoutSomeColumns(columns, 'Performance', _.compact([
      IsAvgPositionDeprecationPhase2Enabled && ColumnAveragePosition,
    ]));
  }

  /**
   * Add the performance category for demographics grids
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addPerformanceCategoryForDemographics({
    IsAvgPositionDeprecationPhase2Enabled,
    IsAvgPositionDeprecationPhase1Enabled,
  }) {
    const withProminence = _.union(
      this.performanceColumns,
      IsAvgPositionDeprecationPhase1Enabled ? [
        ColumnTopImpressionRate,
        ColumnAbsoluteTopImpressionRate,
      ] : []
    );

    return this.addCategoryWithoutSomeColumns(withProminence, 'Performance', _.compact([
      ColumnTotalSpend,
      IsAvgPositionDeprecationPhase2Enabled && ColumnAveragePosition,
    ]));
  }

  /**
   * Add the conversions category (add all conversion metrics when pilot is enabled)
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addConversionsCategory({
    withFluctuations,
    withPartialConversions,
    IsViewThroughConversionEnabled,
    IsViewThroughConversionsRevenueEnabled,
    IsColumnConversionEnabled,
    ColumnFilter,
    isCPSEnabled,
    isInstallColumnsEnabled,
    isConversionValueRuleColumnsEnabled,
  } =
  {
    withFluctuations: false,
    IsViewThroughConversionEnabled: false,
    IsViewThroughConversionsRevenueEnabled: false,
    ColumnFilter: null,
    isCPSEnabled: false,
    isInstallColumnsEnabled: false,
    isConversionValueRuleColumnsEnabled: false,
  }) {
    let columnsToUse;
    if (IsColumnConversionEnabled) {
      const baseConversionColumns =
        getConversionColumns({
          withFluctuations,
          withPartialConversions,
          isCPSEnabled,
          isInstallColumnsEnabled,
          isConversionValueRuleColumnsEnabled,
          isNewCustomerAcquisitionColumnsEnabled: this.isNewCustomerAcquisitionColumnsEnabled,
        });
      columnsToUse = [
        ...baseConversionColumns,

        ...(IsViewThroughConversionEnabled && withPartialConversions ?
          [ColumnViewThroughConversionsCredit] : []),

        ...(IsViewThroughConversionEnabled && !withPartialConversions ?
          [ColumnViewThroughConversions] : []),

        ...(IsViewThroughConversionsRevenueEnabled && withPartialConversions ? [
          ColumnViewThroughConversionsRevenue,
          ColumnViewThroughPartialConversionsCPA,
          ColumnViewThroughConversionsReturnOnAdSpend,
          ColumnViewThroughPartialConversionsRate,
        ] : []),

        ...(IsViewThroughConversionsRevenueEnabled && !withPartialConversions ? [
          ColumnViewThroughConversionsRevenue,
          ColumnViewThroughConversionsCPA,
          ColumnViewThroughConversionsReturnOnAdSpend,
          ColumnViewThroughConversionsRate,
        ] : []),

        ...(this.isNewCustomerAcquisitionColumnsEnabled ? [
          ColumnNewCustomerConversions,
          ColumnNewCustomerRevenue,
          ColumnNewCustomerConversionRate,
          ColumnNewCustomerCPA,
          ColumnNewCustomerRevenueOnAdSpend,
        ] : []),
      ];
    } else {
      columnsToUse = [
        ...(withFluctuations ? conversionsColumnsWithFluctuation : conversionsColumns),
        ...(IsViewThroughConversionEnabled ? [ColumnViewThroughConversions] : []),
        ...(IsViewThroughConversionsRevenueEnabled ? [
          ColumnViewThroughConversionsRevenue,
          ColumnViewThroughConversionsCPA,
          ColumnViewThroughConversionsReturnOnAdSpend,
          ColumnViewThroughConversionsRate,
        ] : []),
      ];
    }

    if (typeof (ColumnFilter) === 'function') {
      columnsToUse = _.filter(columnsToUse, (value, key, index) =>
        ColumnFilter(conversionsColumns, value, key, index));
    }

    return this.addCategory('Conversions', category => category.addColumns(columnsToUse));
  }

  /**
   * Add the competitive category
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addCompetitiveCategoryForKeywords() {
    const columns = _.chain(competitiveColumns)
      .union([
        ColumnExactMatchImpressionSharePercent,
        ColumnTopImpressionSharePercent,
        ColumnTopImpressionShareLostToBudget,
        ColumnTopImpressionShareLostToRank,
        ColumnAbsoluteTopImpressionSharePercent,
        ColumnAbsoluteTopImpressionShareLostToBudget,
        ColumnAbsoluteTopImpressionShareLostToRank,
      ])
      .difference([
        ColumnAuctionLostToAdQualityPercent,
        ColumnAuctionLostToLandingPercent,
        ColumnAuctionLostToBidPercent,
      ])
      .value();

    return this.addCategory('Competitive', category => category.addColumns(columns));
  }

  /**
   * Add the competitive category for campaign or ad groups grid
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addCompetitiveCategoryForCampaignOrAdGroups({
    IsAudienceSOVEnabled,
  }) {
    const columns = _.chain(competitiveColumns)
      .union([
        ColumnExactMatchImpressionSharePercent,
        ColumnTopImpressionSharePercent,
        ColumnTopImpressionShareLostToBudget,
        ColumnTopImpressionShareLostToRank,
        ColumnAbsoluteTopImpressionSharePercent,
        ColumnAbsoluteTopImpressionShareLostToBudget,
        ColumnAbsoluteTopImpressionShareLostToRank,
      ])
      .union(IsAudienceSOVEnabled ? [
        ColumnAudienceAuctionWonPercent,
        ColumnAudienceTopISLostToBudgetPercent,
        ColumnAudienceTopISLostToRankPercent,
        ColumnRelativeCTR,
      ] : [])
      .difference([
        ColumnAuctionLostToAdQualityPercent,
        ColumnAuctionLostToLandingPercent,
        ColumnAuctionLostToBidPercent,
      ])
      .value();

    return this.addCategory('Competitive', category => category.addColumns(columns));
  }

  /**
   * Add the competitive category for performance targets
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addCompetitiveCategoryForPerformanceTargets() {
    return this.addCategoryWithoutSomeColumns(competitiveColumns, 'Competitive', [ColumnClickSharePercent]);
  }

  /**
   * Add the call details category
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addCallDetailsCategory() {
    return this.addCategory('CallDetails', category => category.addColumns(callDetailsColumns));
  }

  /**
   * Add the call details category for campaigns
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addCallDetailsCategoryForCampaigns() {
    return this.addCategoryWithoutSomeColumns(callDetailsColumns, 'CallDetails', [ColumnCallForwarding]);
  }

  /**
   * Add the call details category for performance targets
   * @returns {ColumnsConfigBuilder} - The builder itself
   */
  addCallDetailsCategoryForPerformanceTargets() {
    return this.addCategoryWithoutSomeColumns(callDetailsColumns, 'CallDetails', [ColumnCallForwarding]);
  }
}
